import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { externalLinkOpen } from '../../../../../actions/externallink';
import { Target } from '../../../../../client';
import { getRedirectUrl, RedirectServices } from '../../../../../config/urls';
import { __, __jsx } from '../../../../../i18n/l10n';
import { selectEntitiesMissionsEntityColors } from '../../../../../selectors/entities/missions/entitiesMissionsPrimary.selector';
import {
  selectUserMissionsModerator,
  selectUserPromotionStatus,
} from '../../../../../selectors/user/userPrimary.selector';
import { StateType } from '../../../../../states';
import { PromotionStatuses } from '../../../../../types/promotions';
import { type PromotionStatus } from '../../../../../types/promotions';
import { PoupeeIsolee } from '../../../assets/img';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const NotEnrollableText = styled.div`
  color: white;
  margin: 1em;
  background: #080808;
  border-radius: 1em;
  width: 100%;
  padding: 1em;
  transform: translateY(-30%);
`;

const NotEnrollableImage = styled.img`
  max-width: 75%;
  max-height: 60%;
  padding: 2em;
`;

type NotEnrollableTextLinkWrapperType = {
  $gradientColors: Array<string>;
};

const NotEnrollableTextLinkWrapper = styled.div<NotEnrollableTextLinkWrapperType>`
  display: inline;

  ${({ $gradientColors }) => {
    if ($gradientColors?.length > 1) {
      return css`
        background-image: linear-gradient(to right, ${$gradientColors.join(', ')});
        background-size: 0% 2px;
        background-repeat: no-repeat;
        background-position: left bottom;
        text-decoration: none;
        transition: background-size 200ms ease-in-out;

        & > div {
          color: transparent;

          background-image: linear-gradient(
            to right,
            ${`${$gradientColors.join(', ')} 50%`},
            #156273 50.1%,
            #156273 100%
          );
          background-size: 200%;
          background-position: 100%;
          -webkit-background-clip: text;
          -moz-background-clip: text;
        }

        ${!Target.__MOBILE__
          ? css`
              &:hover {
                background-size: 100% 3px;

                & > div {
                  background-position: 0%;
                }
              }
            `
          : css`
              background-size: 100% 3px;

              & > div {
                background-position: 0%;
              }
            `}
      `;
    }
  }}
`;

const NotEnrollableTextLink = styled.div`
  display: inline;
  transition: none 200ms ease-in-out;
  transition-property: fitler, font-weight, background-position;
  text-decoration: underline;
  color: white;
  font-weight: bold;
`;

type InnerOptInErrorContentProps = {
  missionId: number;
  promotionStatus?: PromotionStatus;
  missionsModerator?: boolean;
};

const InnerOptInErrorContent = ({
  missionId,
  promotionStatus: forcedPromotionStatus,
  missionsModerator: forcedMissionsModerator,
}: InnerOptInErrorContentProps) => {
  const dispatch = useDispatch();

  const gradientSelectorProps = useMemo(() => ({ missionId }), [missionId]);
  const gradientColors = useSelector((state: StateType) =>
    selectEntitiesMissionsEntityColors(state, gradientSelectorProps),
  );
  const reduxPromotionStatus = useSelector(selectUserPromotionStatus);
  const reduxMissionsModerator = useSelector(selectUserMissionsModerator);

  const promotionStatus = useMemo(
    () => forcedPromotionStatus ?? reduxPromotionStatus,
    [forcedPromotionStatus, reduxPromotionStatus],
  );
  const missionsModerator = useMemo(
    () => forcedMissionsModerator ?? reduxMissionsModerator,
    [forcedMissionsModerator, reduxMissionsModerator],
  );

  const onESRMJ30TextClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      dispatch(
        externalLinkOpen(getRedirectUrl(RedirectServices.ES_RM_J30_RESTRICTIONS), {
          auth: true,
          external: true,
        }),
      );
    },
    [dispatch],
  );

  const onGamingLimitsTextClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      dispatch(
        externalLinkOpen(getRedirectUrl(RedirectServices.SET_PROMOTION_LIMITS), {
          auth: true,
          external: true,
        }),
      );
    },
    [dispatch],
  );

  return promotionStatus !== PromotionStatuses.DEFAULT || missionsModerator ? (
    <Wrapper>
      <NotEnrollableImage src={PoupeeIsolee} />
      <NotEnrollableText>
        {/* __('You are currently excluded from betting promotions in your %1$s') */}
        {/* __('!desc:%1$s ES RM J-30 warning') */}

        {promotionStatus === PromotionStatuses.RM_ES_J30
          ? __jsx(
              '!desc:%1$s ES RM J-30 warning',
              <NotEnrollableTextLinkWrapper $gradientColors={gradientColors} onClick={onESRMJ30TextClick}>
                <NotEnrollableTextLink>{__('Warning:')}</NotEnrollableTextLink>
              </NotEnrollableTextLinkWrapper>,
            )
          : null}
        {missionsModerator
          ? __jsx(
              'You are currently excluded from betting promotions in your %1$s',
              <NotEnrollableTextLinkWrapper $gradientColors={gradientColors} onClick={onGamingLimitsTextClick}>
                <NotEnrollableTextLink>{__('gaming limits')}</NotEnrollableTextLink>
              </NotEnrollableTextLinkWrapper>,
            )
          : promotionStatus !== PromotionStatuses.DEFAULT
            ? __('You are currently not eligible to betting promotions')
            : null}
      </NotEnrollableText>
    </Wrapper>
  ) : null;
};

export default InnerOptInErrorContent;
